import { type ColumnsType, type ColumnType, findAndSetColumnSortOrder } from "@similarweb/pro-logic-components";

import {
    AdsenseCell,
    BounceRate,
    DefaultCellRightAlign,
    PagesPerVisit,
    PercentageBarCellRoundedRight,
    RankCell,
    TimeCell,
    TrafficShare,
} from "app/components/React/Table/cells";
import { CategoryCell } from "app/components/React/Table/cells/category-cell/CategoryCell";
import { getIndexCellPropsForTableV2 } from "app/components/React/Table/cells/IndexCell";
import { SegmentOrDomainCellWithoutDomainSign } from "app/components/React/Table/cells/SegmentOrDomainCell";
import type { ITableSort } from "app/components/React/Table/types";
import { PercentageChangeCell } from "app/components/React/TopSites/DomainsTable/PercentageChangeCell";
import { RankChangeCell } from "app/components/React/TopSites/DomainsTable/website-rankings-momentum/components/RankChangeCell";
import type { ILeaderRow } from "app/components/React/TopSites/DomainsTable/website-rankings-momentum/websiteRankings.types";
import { translate } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/ngFilters";
import { ensureDynamicColumnWidth } from "app/components/React/Table/Utils/columns";
import { devicesTypes, type DeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { EPeriodSupport } from "app/UtilitiesAndConstants/UtilityFunctions/periodSupport";

const VISITS_FORMAT = "minVisitsAbbr";

export interface IGetColumnsArgs {
    sort: ITableSort;
    page: number;
    pageSize: number;
    hasSegments: boolean;
    /** Covers "all industries" and custom industries too, not just top-level ones. */
    showIndustryColumn: boolean;
    /** Rendered next to the domain header, as the legacy `showTotalCount` column flag did. */
    totalCount: number;
    webSource: DeviceType;
    /** `null` when the account has no period-over-period support. */
    periodSupport?: EPeriodSupport | null;
}

type LeaderColumn = ColumnType<ILeaderRow>;

/** Column entries are conditionally included, so the builders produce falsy holes. */
type MaybeColumn = LeaderColumn | false | null | undefined;

const getIndexColumn = (page: number, pageSize: number): LeaderColumn =>
    getIndexCellPropsForTableV2<ILeaderRow>(page, pageSize) as LeaderColumn;

const getDomainColumn = (hasSegments: boolean, totalCount: number): LeaderColumn => ({
    dataIndex: "Domain",
    // Suppressed at zero so the count does not flash "(0)" while a page loads.
    title: totalCount
        ? `${translate("analysis.source.referrals.table.columns.domain.title")} (${numberFilter()(totalCount)})`
        : translate("analysis.source.referrals.table.columns.domain.title"),
    sorter: true,
    resize: true,
    fixed: "left",
    width: 185,
    // The legacy column picker excluded fixed columns outright; the new one would show
    // them as locked entries instead, so hide it to keep the picker list identical.
    hiddenInColumnPicker: true,
    tooltipTitle: hasSegments
        ? translate("widget.table.tooltip.topsites.domain.or.segment")
        : translate("widget.table.tooltip.topsites.domain"),
    render: (domain: string, row: ILeaderRow) => (
        <SegmentOrDomainCellWithoutDomainSign
            value={domain}
            row={row}
        />
    ),
});

const getShareColumn = (width: number): LeaderColumn => ({
    dataIndex: "Share",
    title: translate("common.tables.columns.traffic.share.title"),
    sorter: true,
    resize: false,
    width,
    tooltipTitle: translate("widget.table.tooltip.topsites.share"),
    render: (share: number, row: ILeaderRow) => (
        <TrafficShare
            value={share}
            row={row}
        />
    ),
});

/**
 * Rank Change is derived client-side in `transformData`, so there is no backend field to
 * sort on — the column is intentionally not sortable.
 */
/**
 * `isSortable` is false only for industries with segments, where the backend rejects
 * `sort=RankChange` with a 400 because segments carry no rank data.
 *
 * Both the standard and the bonus-country endpoints accept the key. The bonus-country
 * controller was initially reported as not supporting it; verified directly against
 * `/widgetApi/TopSitesNew/TopSites/Table`, where `sort=RankChange` returns 200 and genuinely
 * delta-ordered rows while an unknown key returns 400 (DRI-11168).
 *
 * The value itself is derived and rendered client-side by `computeRankSignals` in every case.
 */
const getRankChangeColumn = (isSortable: boolean): LeaderColumn => ({
    dataIndex: "RankChange",
    title: translate("website.rankings.columns.rank.change.title"),
    sorter: isSortable,
    resize: true,
    align: "right",
    width: 130,
    tooltipTitle: translate("website.rankings.columns.rank.change.tooltip"),
    render: (_rankChange, row: ILeaderRow) => <RankChangeCell row={row} />,
});

const getCategoryColumn = (): LeaderColumn => ({
    dataIndex: "Category",
    title: translate("common.industry"),
    sorter: true,
    resize: true,
    width: 185,
    tooltipTitle: translate("widget.table.tooltip.website_rankings.industry"),
    // `row` supplies `categoryHref`; without it CategoryCell renders the name as plain text.
    render: (category: string, row: ILeaderRow) => (
        <CategoryCell
            value={category}
            row={row}
        />
    ),
});

const getRootCategoryRankColumn = (): LeaderColumn => ({
    dataIndex: "RootCategoryRank",
    title: translate("common.tables.columns.industry.rank.title"),
    sorter: true,
    resize: true,
    align: "right",
    width: 130,
    tooltipTitle: translate("widget.table.tooltip.website_rankings.industry_rank"),
    render: (rank: number) => <RankCell value={rank} />,
});

const getPeriodChangeColumn = (periodSupport: EPeriodSupport): LeaderColumn => {
    const isYearly = periodSupport === EPeriodSupport.Yearly;
    return {
        dataIndex: "PeriodChange",
        title: isYearly
            ? translate("analysis.source.search.all.table.columns.yearly.change.title")
            : translate("analysis.source.search.all.table.columns.quarterly.change.title"),
        sorter: true,
        width: isYearly ? 118 : 130,
        tooltipTitle: isYearly
            ? translate("analysis.source.search.all.table.columns.yearly.change.tooltip")
            : translate("analysis.source.search.all.table.columns.quarterly.change.tooltip"),
        render: (change: number) => <PercentageChangeCell value={change} />,
    };
};

/**
 * Columns whose backend ordering runs opposite to the arrow antd would draw. The Leaders
 * endpoint treats `asc=false` on a rank as "best rank first", so it answers 1, 2, 3 under a
 * descending arrow. Flipping only the rendered `sortOrder` keeps the glyph agreeing with the
 * numbers; the request is untouched. This is what the legacy config's `inverted` flag did.
 *
 * Bounce rate carried `inverted` too but does not belong here — it really does come back
 * numerically descending, so its arrow is already correct.
 */
const INVERTED_SORT_ORDER_COLUMNS = new Set(["RootCategoryRank"]);

/** Drops the conditional holes and applies the controlled sort order and width fill. */
const finalizeColumns = (columns: MaybeColumn[], sort: ITableSort): ColumnsType<ILeaderRow> => {
    const resolved = columns
        .filter((column): column is LeaderColumn => Boolean(column))
        .map((column) =>
            findAndSetColumnSortOrder<ILeaderRow>(
                // Descending first (the legacy table's default), and the repeated trailing
                // entry makes the cycle loop instead of ending in antd's "cleared" state:
                // antd picks the next direction with `sortDirections[indexOf(current) + 1]`,
                // and `indexOf` matches the first entry, so descend -> ascend -> descend...
                // Without the third entry that lookup runs off the end and yields a cleared
                // sort with no `columnKey`, which the API rejects (`sort=undefined` -> 400).
                column.sorter ? { ...column, sortDirections: ["descend", "ascend", "descend"] } : column,
                sort.field,
                INVERTED_SORT_ORDER_COLUMNS.has(String(column.dataIndex))
                    ? sort.sortDirection === "desc"
                    : sort.sortDirection === "asc",
            ),
        );
    ensureDynamicColumnWidth(resolved);
    return resolved;
};

export const getColumns = ({
    sort,
    page,
    pageSize,
    periodSupport,
    hasSegments,
    showIndustryColumn,
    totalCount,
    webSource,
}: IGetColumnsArgs): ColumnsType<ILeaderRow> => {
    const isTotalWebSource = webSource === devicesTypes.TOTAL;
    const showPeriodChange = Boolean(periodSupport) && periodSupport !== EPeriodSupport.Monthly;

    return finalizeColumns(
        [
            getIndexColumn(page, pageSize),
            getDomainColumn(hasSegments, totalCount),
            getShareColumn(143),
            {
                dataIndex: "MoMChange",
                title: translate("analysis.source.search.all.table.columns.month.over.month.change.title"),
                sorter: true,
                resize: true,
                ellipsis: true,
                width: 158,
                tooltipTitle: translate("widget.table.tooltip.topsites.change"),
                render: (change: number | undefined) => <PercentageChangeCell value={change} />,
            },
            getRankChangeColumn(!hasSegments),
            !hasSegments && showIndustryColumn && getCategoryColumn(),
            !hasSegments && getRootCategoryRankColumn(),
            {
                dataIndex: "AvgMonthVisits",
                title: translate("common.tables.columns.monthly.visits.title"),
                sorter: true,
                resize: true,
                ellipsis: true,
                width: 130,
                tooltipTitle: translate("widget.table.tooltip.monthlyvisits"),
                render: (visits: number) => (
                    <DefaultCellRightAlign
                        value={visits}
                        format={VISITS_FORMAT}
                    />
                ),
            },
            {
                dataIndex: "UniqueUsers",
                // Hidden, not omitted: an omitted column also vanishes from the column picker,
                // whereas the legacy config kept it listed (toggled off) for Total.
                hidden: isTotalWebSource,
                title: translate("industry.trends.table.columns.unique.users"),
                sorter: true,
                resize: true,
                ellipsis: true,
                width: 155,
                tooltipTitle: translate("industry.trends.table.columns.unique.users.tooltip"),
                render: (uniqueUsers: number) => (
                    <DefaultCellRightAlign
                        value={uniqueUsers}
                        format={VISITS_FORMAT}
                    />
                ),
            },
            isTotalWebSource && {
                dataIndex: "DeduplicatedAudience",
                title: translate("industry.trends.table.columns.deduplicated.audience"),
                sorter: true,
                resize: true,
                ellipsis: true,
                width: 155,
                tooltipTitle: translate("industry.trends.table.columns.unique.users.tooltip"),
                render: (audience: number) => (
                    <DefaultCellRightAlign
                        value={audience}
                        format={VISITS_FORMAT}
                    />
                ),
            },
            showPeriodChange && getPeriodChangeColumn(periodSupport as EPeriodSupport),
            !hasSegments &&
                isTotalWebSource && {
                    dataIndex: "DesktopMobileShare",
                    title: translate("common.tables.columns.desktop.mobile.share.title"),
                    // The legacy config read `sortable: "False"`, a truthy string, so this
                    // column was sortable — and the backend genuinely supports the key: it
                    // orders by the desktop component. Verified against staging, where
                    // `sort=DesktopMobileShare` returns 200 and correctly ordered rows while
                    // an unknown key returns 400. Despite how that config value reads, the
                    // sort works, so keep it.
                    sorter: true,
                    align: "right",
                    width: 190,
                    tooltipTitle: translate("wa.ao.desktopvsmobile.tooltip"),
                    render: (share: [number, number]) => <PercentageBarCellRoundedRight value={share} />,
                },
            {
                dataIndex: "AvgVisitDuration",
                title: translate("common.tables.columns.visit.duration.title"),
                sorter: true,
                resize: true,
                ellipsis: true,
                align: "right",
                width: 130,
                tooltipTitle: translate("widget.table.tooltip.topsites.avgvisitduration"),
                render: (duration: number) => <TimeCell value={duration} />,
            },
            {
                dataIndex: "PagesPerVisit",
                title: translate("common.tables.columns.page.per.visit.title"),
                sorter: true,
                resize: true,
                ellipsis: true,
                width: 120,
                tooltipTitle: translate("widget.table.tooltip.topsites.ppv"),
                render: (pagesPerVisit: number) => <PagesPerVisit value={pagesPerVisit} />,
            },
            {
                dataIndex: "BounceRate",
                title: translate("common.tables.columns.bounce.rate.title"),
                sorter: true,
                resize: true,
                ellipsis: true,
                width: 125,
                tooltipTitle: translate("widget.table.tooltip.topsites.bouncerate"),
                render: (bounceRate: number) => <BounceRate value={bounceRate} />,
            },
            {
                dataIndex: "HasAdsense",
                title: translate("common.tables.columns.adsense.title"),
                sorter: true,
                resize: true,
                ellipsis: true,
                width: 100,
                render: (hasAdsense: boolean) => <AdsenseCell value={hasAdsense} />,
            },
        ],
        sort,
    );
};

/**
 * Bonus countries are served by a different endpoint that only returns the ranking core,
 * so the table collapses to six columns.
 */
export const getBonusCountryColumns = ({
    sort,
    page,
    pageSize,
    hasSegments,
    showIndustryColumn,
    totalCount,
}: Omit<IGetColumnsArgs, "webSource" | "periodSupport">): ColumnsType<ILeaderRow> =>
    finalizeColumns(
        [
            getIndexColumn(page, pageSize),
            getDomainColumn(hasSegments, totalCount),
            getShareColumn(150),
            getRankChangeColumn(!hasSegments),
            !hasSegments && showIndustryColumn && getCategoryColumn(),
            !hasSegments && getRootCategoryRankColumn(),
        ],
        sort,
    );
